/**
 * Assets service — the `assets` rows (a file's name, URL, MIME type, size and
 * metadata). The FILE itself — the presigned S3 upload, the CloudFront URL —
 * is an interface's concern, not the domain's; this writes only the row. An
 * upload is the row's side only (`upload`): the caller presigns and moves the
 * bytes.
 *
 * Attaching an asset to a record is a different table (`record_attachments`)
 * and goes through the relation writes, not here.
 */

import type { ResultOf } from "@graphql-typed-document-node/core"
import { assetKind, type AssetView } from "../fields/assets.js"
import {
  countAssets,
  deleteAsset,
  deleteAssets,
  fetchAsset,
  fetchAssetAttachments,
  fetchAssetsWhere,
  fetchRecordsOfType,
  fetchRecordsWithDataKey,
  insertAsset,
  searchAssets,
  updateAsset,
} from "../gql/assets.js"
import type { Assets_Bool_Exp, Assets_Order_By } from "../gql/generated/schema.js"
import type { Mutate, MutateOptions, Query } from "../index.js"

/** An assets row as every read and write here returns it: AssetFields. */
export type AssetItem = NonNullable<ResultOf<typeof fetchAsset>["asset"]>

export interface CreateAssetInput {
  url: string
  name: string
  mime_type?: string | null
  size?: number | null
  metadata?: Record<string, unknown> | null
}

/** Only the keys present are written. */
export interface UpdateAssetPatch {
  name?: string
  url?: string
  mime_type?: string | null
  size?: number | null
  metadata?: Record<string, unknown> | null
}

/** What a media file reports about itself: pixels for images and video, seconds for video and audio. */
export interface MediaDimensions {
  width?: number
  height?: number
  duration?: number
}

/** A presigned upload: where to PUT the file, and the object it creates. */
export interface UploadTicket {
  putUrl: string
  /** The object key; recorded as `metadata.s3_key`. */
  key: string
  /** The object's S3 URL; stored as the row's `url`. */
  url: string
  bucket: string
}

export interface UploadAssetInput {
  file: { name: string; size: number; type: string }
  /** Recorded as `metadata.origin`: manual_upload, url_upload, mcp_upload, … */
  origin: string
  /** Media dimensions (width, height, duration) recorded in the metadata. */
  dimensions?: MediaDimensions | null
  /** Asks for a presigned upload; run before any row exists. */
  presign: () => Promise<UploadTicket>
  /** Moves the file's bytes to the ticket. The caller's part: a browser reports
   *  progress, the MCP sends a Blob. Reject to abandon the upload. */
  put: (ticket: UploadTicket) => Promise<void>
}

export interface RemoveAssetsResult {
  removedCount: number
  removedIds: number[]
  missingIds: number[]
}

export type { AssetView }

/** A library sort: a column, newest/largest first by default. */
export interface AssetSort {
  by: "created_at" | "name" | "size"
  direction?: "asc" | "desc"
}

export interface AssetSearch {
  /** Matched against the file name and its storage path. */
  text?: string
  view?: AssetView | null
  /** `metadata.origin`: manual_upload, s3_sync, harmonic_enrichment, … */
  origin?: string | null
  /** true: used by at least one record; false: by none. */
  attached?: boolean | null
  sort?: AssetSort
  limit?: number
  offset?: number
}

/** A search hit: the asset row, and how many records use it. */
export type AssetSearchItem = ResultOf<typeof searchAssets>["assets"][number]

export interface AssetSearchResult {
  items: AssetSearchItem[]
  total: number
  totalSize: number
}

export type AssetCounts = Record<"all" | AssetView | "unattached", number>

/** A record using an asset, through `schema_key` (null: no field). */
export type AssetAttachment = ResultOf<typeof fetchAssetAttachments>["attachments"][number]

const MEDIA_PREFIXES: Record<Exclude<AssetView, "document">, string> = {
  image: "image/%",
  audio: "audio/%",
  video: "video/%",
}

function orderBy({ by, direction = by === "name" ? "asc" : "desc" }: AssetSort): Assets_Order_By[] {
  const nulls = direction === "asc" ? "asc_nulls_last" : "desc_nulls_last"
  return [{ [by]: by === "size" ? nulls : direction }, { id: direction }]
}

/** The `assets` filter for a library search. */
export function assetWhere(search: AssetSearch = {}): Assets_Bool_Exp {
  const and: Assets_Bool_Exp[] = []
  const text = search.text?.trim()
  if (text) {
    const pattern = `%${text.replace(/[%_\\]/g, (c) => `\\${c}`)}%`
    and.push({ _or: [{ name: { _ilike: pattern } }, { url: { _ilike: pattern } }] })
  }
  if (search.view === "document") {
    and.push({
      _or: [
        { mime_type: { _is_null: true } },
        { _not: { _or: Object.values(MEDIA_PREFIXES).map((p) => ({ mime_type: { _ilike: p } })) } },
      ],
    })
  } else if (search.view) {
    and.push({ mime_type: { _ilike: MEDIA_PREFIXES[search.view] } })
  }
  if (search.origin) and.push({ metadata: { _contains: { origin: search.origin } } })
  if (search.attached === true) and.push({ attachments: {} })
  if (search.attached === false) and.push({ _not: { attachments: {} } })
  return and.length ? { _and: and } : {}
}

/**
 * How an unattached file finds its record. `dataKey`: the record's `data[key]`
 * holds the file name, so the same name is a sure match and the same name with
 * another extension (the episode's video, its transcript) is too. `folder`: the
 * file sits in a folder named for the record's slug, worth a look.
 */
export type MediaMatcher =
  | { type: string; dataKey: string }
  | { type: string; folder: true; kinds: string[] }

export const MEDIA_MATCHERS: MediaMatcher[] = [
  { type: "episode", dataKey: "podcast_file_key" },
  { type: "show", folder: true, kinds: ["image"] },
]

export interface MediaMatch {
  asset: AssetItem
  record: { id: number; name: string; slug: string | null; type: string } | null
  confidence: "sure" | "check" | "none"
  /** Why it matched, for the reviewer. */
  reason: string
  /** The attachment label to write: the file's kind (audio, video, transcript, cover). */
  label: string
}

const stem = (name: string) => name.replace(/\.[^./]+$/, "").toLowerCase()

/** The folder holding a file: its storage key's second-to-last segment. */
export function assetFolder(asset: Pick<AssetItem, "url" | "metadata">): string | null {
  const key = (asset.metadata as { s3_key?: string } | null)?.s3_key ?? asset.url
  const parts = String(key ?? "")
    .split("/")
    .filter(Boolean)
  return parts.length > 1 ? parts[parts.length - 2]! : null
}

function labelFor(asset: AssetItem, fallback: string) {
  const kind = assetKind(asset.mime_type)
  if (kind === "text") return "transcript"
  if (kind === "audio" || kind === "video") return kind
  return fallback
}

/** Pair each asset with the record a matcher finds for it; pure, for review. */
export function matchAssets(
  assets: AssetItem[],
  candidates: {
    byDataKey: Array<{ record: MediaMatch["record"] & {}; value: unknown }>
    byFolder: Array<{ record: MediaMatch["record"] & {}; kinds: string[] }>
  }
): MediaMatch[] {
  const exact = new Map<string, MediaMatch["record"] & {}>()
  const byStem = new Map<string, MediaMatch["record"] & {}>()
  for (const { record, value } of candidates.byDataKey) {
    if (typeof value !== "string" || !value) continue
    exact.set(value.toLowerCase(), record)
    byStem.set(stem(value), record)
  }
  const folders = new Map(
    candidates.byFolder.map((c) => [c.record.slug?.toLowerCase() ?? "", c] as const)
  )

  return assets.map((asset): MediaMatch => {
    const name = (asset.name ?? "").toLowerCase()
    const hit = exact.get(name)
    if (hit)
      return {
        asset,
        record: hit,
        confidence: "sure",
        reason: "File name matches",
        label: labelFor(asset, "file"),
      }
    const sibling = byStem.get(stem(name))
    if (sibling)
      return {
        asset,
        record: sibling,
        confidence: "sure",
        reason: "Same name as its audio file",
        label: labelFor(asset, "file"),
      }
    const folder = assetFolder(asset)?.toLowerCase()
    const show = folder ? folders.get(folder) : undefined
    if (show && show.kinds.includes(assetKind(asset.mime_type)))
      return {
        asset,
        record: show.record,
        confidence: "check",
        reason: "Folder matches",
        label: "cover",
      }
    return {
      asset,
      record: null,
      confidence: "none",
      reason: "No record found",
      label: labelFor(asset, "file"),
    }
  })
}

export function createAssetsService({ query, mutate }: { query: Query; mutate: Mutate }) {
  const get = async (id: number): Promise<AssetItem | null> => {
    return (await query(fetchAsset, { id })).asset ?? null
  }

  const create = async (input: CreateAssetInput): Promise<AssetItem> => {
    const object: Record<string, unknown> = { url: input.url, name: input.name }
    if (input.mime_type !== undefined) object.mime_type = input.mime_type
    if (input.size !== undefined) object.size = input.size
    if (input.metadata !== undefined) object.metadata = input.metadata

    const row = (await mutate(insertAsset, { object })).insert_assets_one
    if (!row) throw new Error("Failed to create asset record")
    return row
  }

  const update = async (id: number, patch: UpdateAssetPatch): Promise<AssetItem> => {
    const row = (await mutate(updateAsset, { id, _set: patch })).update_assets_by_pk
    if (!row) throw new Error(`Asset with id ${id} not found`)
    return row
  }

  /**
   * The asset row's side of an upload. The file moves in the caller's `put`;
   * this owns what the row says at each stage: created with
   * `upload_complete: false`, then marked complete once the bytes are there.
   * A failure after the row exists removes it again; if that removal also
   * fails, the error names the orphaned id.
   */
  const upload = async (input: UploadAssetInput): Promise<AssetItem> => {
    const { file, origin, dimensions, presign, put } = input
    const ticket = await presign()
    const created = await create({
      url: ticket.url,
      name: file.name,
      mime_type: file.type,
      size: file.size,
      metadata: {
        s3_key: ticket.key,
        s3_bucket: ticket.bucket,
        original_filename: file.name,
        origin,
        upload_complete: false,
        ...dimensions,
      },
    })

    try {
      await put(ticket)
      return await update(created.id, {
        metadata: {
          ...(created.metadata as Record<string, unknown> | null),
          upload_complete: true,
          uploaded_at: new Date().toISOString(),
        },
      }).catch((err: unknown) => {
        if (err instanceof Error && err.message === `Asset with id ${created.id} not found`) {
          throw new Error("Failed to mark upload as complete", { cause: err })
        }
        throw err
      })
    } catch (err) {
      try {
        await remove(created.id)
      } catch (cleanupErr) {
        const original = err instanceof Error ? err.message : String(err)
        const cleanup = cleanupErr instanceof Error ? cleanupErr.message : String(cleanupErr)
        throw new Error(
          `${original} (additionally, asset cleanup failed for orphaned id=${created.id}: ${cleanup})`,
          { cause: cleanupErr }
        )
      }
      throw err
    }
  }

  /** Resolves to the deleted row's id, or null when no such asset existed —
   *  deleting an absent asset is not an error.
   *
   *  Deleting a file is for admins: pass AS_ADMIN. Without it the write runs
   *  as the caller's default role, and staff may delete only an upload that
   *  failed half way: no attachment uses it and its metadata still says
   *  upload_complete false. */
  const remove = async (id: number, opts?: MutateOptions): Promise<{ id: number } | null> => {
    return (await mutate(deleteAsset, { id }, opts)).delete_assets_by_pk ?? null
  }

  /** `missingIds` are the requested ids that deleted nothing (never existed,
   *  or hidden from the caller). Admins pass AS_ADMIN, as for `remove`. */
  const removeMany = async (ids: number[], opts?: MutateOptions): Promise<RemoveAssetsResult> => {
    if (!ids.length) return { removedCount: 0, removedIds: [], missingIds: [] }

    const result = (await mutate(deleteAssets, { ids }, opts)).delete_assets
    // Fail loud on a null mutation root rather than reporting "removed nothing".
    if (!result) throw new Error("deleteAssets: Hasura returned no delete_assets result")

    const removedIds = result.returning.map((row) => row.id)
    const removed = new Set(removedIds)
    return {
      removedCount: result.affected_rows,
      removedIds,
      missingIds: ids.filter((id) => !removed.has(id)),
    }
  }

  /** One page of the library. */
  const search = async (opts: AssetSearch = {}): Promise<AssetSearchResult> => {
    const data = await query(searchAssets, {
      where: assetWhere(opts),
      limit: opts.limit ?? 60,
      offset: opts.offset ?? 0,
      order_by: orderBy(opts.sort ?? { by: "created_at" }),
    })
    return {
      items: data.assets,
      total: data.total.aggregate?.count ?? 0,
      totalSize: Number(data.total.aggregate?.sum?.size ?? 0),
    }
  }

  /** How many files each library view holds. */
  const counts = async (): Promise<AssetCounts> => {
    const data = await query(countAssets, {
      all: {},
      image: assetWhere({ view: "image" }),
      audio: assetWhere({ view: "audio" }),
      video: assetWhere({ view: "video" }),
      document: assetWhere({ view: "document" }),
      unattached: assetWhere({ attached: false }),
    })
    return {
      all: data.all.aggregate?.count ?? 0,
      image: data.image.aggregate?.count ?? 0,
      audio: data.audio.aggregate?.count ?? 0,
      video: data.video.aggregate?.count ?? 0,
      document: data.document.aggregate?.count ?? 0,
      unattached: data.unattached.aggregate?.count ?? 0,
    }
  }

  /** The records an asset is attached to. */
  const usage = async (id: number): Promise<AssetAttachment[]> =>
    (await query(fetchAssetAttachments, { assetId: id })).attachments

  /** Every unattached file whose path contains `folder` (all of them without
   *  one), each paired with the record MEDIA_MATCHERS finds for it. */
  const matchUnattached = async (
    opts: { folder?: string; matchers?: MediaMatcher[] } = {}
  ): Promise<MediaMatch[]> => {
    const matchers = opts.matchers ?? MEDIA_MATCHERS
    const where: Assets_Bool_Exp = { _and: [assetWhere({ attached: false })] }
    if (opts.folder) where._and!.push({ url: { _ilike: `%/${opts.folder}/%` } })

    const [assets, byDataKey, byFolder] = await Promise.all([
      query(fetchAssetsWhere, { where }).then((d) => d.assets),
      Promise.all(
        matchers.flatMap((m) =>
          "dataKey" in m
            ? [
                query(fetchRecordsWithDataKey, { type: m.type, key: m.dataKey }).then((d) =>
                  d.records.map(({ value, ...record }) => ({ record, value }))
                ),
              ]
            : []
        )
      ).then((lists) => lists.flat()),
      Promise.all(
        matchers.flatMap((m) =>
          "folder" in m
            ? [
                query(fetchRecordsOfType, { type: m.type }).then((d) =>
                  d.records.map((record) => ({ record, kinds: m.kinds }))
                ),
              ]
            : []
        )
      ).then((lists) => lists.flat()),
    ])
    return matchAssets(assets, { byDataKey, byFolder })
  }

  return { get, create, update, upload, remove, removeMany, search, counts, usage, matchUnattached }
}
